import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, FolderLock } from "lucide-react";
import { getAdminPlans, getAdminPlanDetail } from "./plannerApi";
import { formatCompact } from "./format";
import StepProjection from "./StepProjection";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export const PlannerPlansAdmin = () => {
    const [plans, setPlans] = useState(null);
    const [detail, setDetail] = useState(null);

    useEffect(() => { getAdminPlans().then(setPlans).catch(() => toast.error("Could not load submitted plans.")); }, []);

    const open = async (id) => {
        try { setDetail(await getAdminPlanDetail(id)); }
        catch (e) { toast.error(e?.response?.data?.detail || "Could not open plan."); }
    };

    if (!plans) return <div className="font-editorial italic text-oth-ink3">Loading…</div>;

    if (detail) {
        const answers = detail.payload?.profile || [];
        return (
            <div className="space-y-6" data-testid="admin-plan-detail">
                <button onClick={() => setDetail(null)} className="inline-flex items-center gap-2 text-sm font-body text-oth-ink2 hover:text-oth-ink" data-testid="plan-detail-back"><ArrowLeft size={14} /> All submitted plans</button>
                <div className="rounded-2xl border border-oth-line/70 bg-white p-6">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <div className="font-display text-xl font-semibold tracking-wide">{detail.code}</div>
                            <div className="text-sm text-oth-ink3 font-body mt-1">
                                {detail.contact?.name || "Anonymous"} · {detail.contact?.email || "no email"} · {detail.contact?.phone || "no phone"} · submitted {fmtDate(detail.submitted_at)}
                            </div>
                        </div>
                    </div>
                    {answers.length > 0 && (
                        <div className="mt-5 pt-5 border-t border-oth-line/60">
                            <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-3">Risk profile answers</div>
                            <div className="space-y-2.5">
                                {answers.map((a) => (
                                    <div key={a.question_id} className="text-sm font-body">
                                        <span className="text-oth-ink3">{a.label}</span>
                                        <div className="font-medium">{String(a.value)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <StepProjection projection={detail.projection} />
            </div>
        );
    }

    return (
        <div data-testid="planner-plans-admin">
            {plans.length === 0 ? (
                <div className="rounded-2xl border border-oth-line/70 bg-white p-10 text-center">
                    <FolderLock size={28} className="mx-auto text-oth-ink3 mb-3" />
                    <div className="font-display font-semibold">No submitted plans yet</div>
                    <p className="text-sm text-oth-ink3 font-body mt-1 max-w-md mx-auto">Plans only appear here when a user explicitly clicks "Submit my plan to OTH". Unsubmitted plans remain encrypted and invisible to admin.</p>
                </div>
            ) : (
                <div className="rounded-2xl border border-oth-line/70 bg-white overflow-hidden">
                    <table className="w-full text-sm font-body">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-oth-ink3 border-b border-oth-line/70">
                                <th className="px-5 py-3.5 font-medium">Code</th>
                                <th className="px-5 py-3.5 font-medium">Contact</th>
                                <th className="px-5 py-3.5 font-medium text-right">Starting Wealth</th>
                                <th className="px-5 py-3.5 font-medium">Submitted</th>
                                <th className="px-5 py-3.5" />
                            </tr>
                        </thead>
                        <tbody>
                            {plans.map((p) => (
                                <tr key={p.id} className="border-b border-oth-line/40 hover:bg-[#fafaf8]" data-testid={`plan-row-${p.code}`}>
                                    <td className="px-5 py-3.5 font-semibold tracking-wide">{p.code}</td>
                                    <td className="px-5 py-3.5">
                                        <div className="font-medium">{p.contact?.name || "Anonymous"}</div>
                                        <div className="text-xs text-oth-ink3">{p.contact?.email || p.contact?.phone || "—"}</div>
                                    </td>
                                    <td className="px-5 py-3.5 text-right tabular-nums">{formatCompact(p.starting_wealth)}</td>
                                    <td className="px-5 py-3.5 text-oth-ink3">{fmtDate(p.submitted_at)}</td>
                                    <td className="px-5 py-3.5 text-right">
                                        <button onClick={() => open(p.id)} className="btn-pill btn-pill-dark text-xs" data-testid={`plan-open-${p.code}`}>View plan</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
